import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Navigation, Gauge, Search, AlertTriangle, MapPin, RefreshCw,
  Car as CarIcon, Pause, Activity, Route as RouteIcon, X,
} from "lucide-react";
import { toast } from "sonner";

const ANCHOR_LNG = 8.5809;
const ANCHOR_LAT = 53.5396;
const FENCE_RADIUS_KM = 50;
const REFRESH_MS = 12000;

const STATUS_COLOR = { parked: "#737373", city: "#0891B2", highway: "#E11226" };
const STATUS_LABEL = { parked: "Parkend", city: "Stadt", highway: "Schnellstraße" };

function circleGeoJSON(lng, lat, radiusKm, steps = 64) {
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    const dx = (radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180))) * Math.cos(a);
    const dy = (radiusKm / 110.574) * Math.sin(a);
    coords.push([lng + dx, lat + dy]);
  }
  return { type: "Feature", geometry: { type: "Polygon", coordinates: [coords] } };
}

function makePinEl(color, heading, isActive, onClick) {
  const el = document.createElement("div");
  el.style.cssText = "width:28px;height:28px;cursor:pointer;";
  el.innerHTML = `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg"
    style="filter:drop-shadow(0 1px 4px rgba(0,0,0,.5))">
    ${isActive ? `<circle cx="14" cy="14" r="13" fill="${color}" fill-opacity="0.2"/>` : ""}
    <g transform="rotate(${heading} 14 14)">
      <path d="M14 4 L20 23 L14 18 L8 23 Z" fill="${color}" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/>
    </g>
  </svg>`;
  el.addEventListener("click", onClick);
  return el;
}

export default function AdminTracking() {
  const { vehicleId: selectedFromUrl } = useParams();
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});   // vehicleId → { marker, popup }
  const [fleet, setFleet] = useState([]);
  const [selectedId, setSelectedId] = useState(selectedFromUrl || null);
  const [track, setTrack] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [simEnabled, setSimEnabled] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const pollRef = useRef(null);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const toggleSim = async () => {
    try {
      const { data } = await api.post("/admin/gps-simulator/toggle");
      setSimEnabled(data.enabled);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    api.get("/admin/gps-simulator/status").then(({ data }) => setSimEnabled(data.enabled)).catch(() => {});
  }, []);

  // ── Init MapLibre ─────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "© OpenStreetMap",
          },
        },
        layers: [{ id: "osm", type: "raster", source: "osm" }],
      },
      center: [ANCHOR_LNG, ANCHOR_LAT],
      zoom: 10,
    });

    map.addControl(new maplibregl.NavigationControl(), "bottom-right");

    map.on("load", () => {
      map.addSource("fence", { type: "geojson", data: circleGeoJSON(ANCHOR_LNG, ANCHOR_LAT, FENCE_RADIUS_KM) });
      map.addLayer({ id: "fence-fill", type: "fill", source: "fence", paint: { "fill-color": "#A3A3A3", "fill-opacity": 0.04 } });
      map.addLayer({ id: "fence-line", type: "line", source: "fence", paint: { "line-color": "#A3A3A3", "line-width": 1, "line-dasharray": [4, 6] } });

      map.addSource("track", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: [[0,0],[0,0]] } } });
      map.addLayer({
        id: "track-line", type: "line", source: "track",
        paint: { "line-color": "#E11226", "line-width": 3, "line-opacity": 0.85 },
        layout: { "line-cap": "round", "line-join": "round" },
      });

      mapRef.current = map;
      setMapReady(true);
    });

    return () => {
      Object.values(markersRef.current).forEach(({ marker }) => marker.remove());
      markersRef.current = {};
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  // ── Sync markers with fleet ───────────────────────────────────────
  const selectVehicle = useCallback((vid) => {
    setSelectedId(vid);
    navigate(vid ? `/admin/tracking/${vid}` : "/admin/tracking", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || fleet.length === 0) return;

    const currentIds = new Set(fleet.map((v) => v.vehicle_id));

    // Remove stale markers
    Object.keys(markersRef.current).forEach((id) => {
      if (!currentIds.has(id)) {
        markersRef.current[id].marker.remove();
        delete markersRef.current[id];
      }
    });

    fleet.forEach((v) => {
      const color = STATUS_COLOR[v.status] || "#737373";
      const isActive = v.vehicle_id === selectedIdRef.current;
      const lngLat = [v.lng, v.lat];

      if (markersRef.current[v.vehicle_id]) {
        // Update position + rebuild SVG pin
        const { marker } = markersRef.current[v.vehicle_id];
        marker.setLngLat(lngLat);
        const el = marker.getElement();
        el.innerHTML = `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg"
          style="filter:drop-shadow(0 1px 4px rgba(0,0,0,.5))">
          ${isActive ? `<circle cx="14" cy="14" r="13" fill="${color}" fill-opacity="0.2"/>` : ""}
          <g transform="rotate(${v.heading || 0} 14 14)">
            <path d="M14 4 L20 23 L14 18 L8 23 Z" fill="${color}" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/>
          </g>
        </svg>`;
        // Update popup content
        markersRef.current[v.vehicle_id].popup.setHTML(
          `<div style="font-size:12px;line-height:1.6"><b>${v.brand} ${v.name}</b><br>${STATUS_LABEL[v.status] || v.status} · ${Math.round(v.speed_kmh)} km/h</div>`
        );
      } else {
        // Create new marker
        const el = makePinEl(color, v.heading || 0, isActive, () => selectVehicle(v.vehicle_id));
        const popup = new maplibregl.Popup({ offset: 16, closeButton: false }).setHTML(
          `<div style="font-size:12px;line-height:1.6"><b>${v.brand} ${v.name}</b><br>${STATUS_LABEL[v.status] || v.status} · ${Math.round(v.speed_kmh)} km/h</div>`
        );
        const marker = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat(lngLat)
          .setPopup(popup)
          .addTo(map);
        markersRef.current[v.vehicle_id] = { marker, popup };
      }
    });
  }, [fleet, mapReady, selectedId, selectVehicle]);

  // ── Track line ────────────────────────────────────────────────────
  // Redraw track whenever mapReady flips (map just loaded, track data already in state)
  useEffect(() => {
    if (mapReady) drawTrack(track);
  }, [mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fly to selected ───────────────────────────────────────────────
  const selected = useMemo(() => fleet.find((v) => v.vehicle_id === selectedId) || null, [fleet, selectedId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !selected) return;
    map.flyTo({ center: [selected.lng, selected.lat], zoom: Math.max(map.getZoom(), 14), duration: 800 });
  }, [selected, mapReady]);

  // ── Filtered list & stats ─────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = fleet;
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((v) =>
        v.name.toLowerCase().includes(q) ||
        v.brand.toLowerCase().includes(q) ||
        (v.category || "").toLowerCase().includes(q)
      );
    }
    if (filter === "alert") list = list.filter((v) => v.geofence_alert);
    else if (filter !== "all") list = list.filter((v) => v.status === filter);
    return list;
  }, [fleet, query, filter]);

  const stats = useMemo(() => {
    const c = { total: fleet.length, parked: 0, city: 0, highway: 0, alerts: 0 };
    fleet.forEach((v) => {
      if (v.status === "parked") c.parked++;
      else if (v.status === "city") c.city++;
      else if (v.status === "highway") c.highway++;
      if (v.geofence_alert) c.alerts++;
    });
    return c;
  }, [fleet]);

  // ── Data loading ──────────────────────────────────────────────────
  const loadFleet = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/fleet/locations");
      setFleet(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  const drawTrack = useCallback((data) => {
    const map = mapRef.current;
    if (!map) return;
    const coords = (Array.isArray(data) ? data : [])
      .filter((p) => p.lat != null && p.lng != null)
      .map((p) => [parseFloat(p.lng), parseFloat(p.lat)]);
    const geojson = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: coords.length >= 2 ? coords : [[0, 0], [0, 0]],
      },
    };
    const src = map.getSource("track");
    if (src) {
      src.setData(geojson);
    } else {
      // Source not ready yet — retry after map finishes loading
      map.once("idle", () => {
        const s = map.getSource("track");
        if (s) s.setData(geojson);
      });
    }
  }, []);

  const loadTrack = useCallback(async (vid) => {
    if (!vid) return;
    try {
      const { data } = await api.get(`/admin/vehicles/${vid}/track?limit=40`);
      const arr = Array.isArray(data) ? data : [];
      setTrack(arr);
      drawTrack(arr);
    } catch (e) {
      setTrack([]);
      toast.error("Track-Daten: " + apiError(e));
    }
  }, [drawTrack]);

  useEffect(() => { loadFleet(); }, [loadFleet]);

  useEffect(() => {
    if (paused) return;
    pollRef.current = setInterval(() => {
      loadFleet();
      if (selectedIdRef.current) loadTrack(selectedIdRef.current);
    }, REFRESH_MS);
    return () => clearInterval(pollRef.current);
  }, [paused, loadFleet, loadTrack]);

  useEffect(() => {
    if (selectedId) {
      loadTrack(selectedId);
    } else {
      setTrack([]);
      drawTrack([]);
    }
  }, [selectedId, loadTrack, drawTrack]);

  return (
    <div className="rf-fade-in" data-testid="admin-tracking">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#E11226] font-semibold">Flotte · Live</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0A0A0A] mt-1">GPS-Tracking</h1>
          <p className="text-sm text-[#525252] mt-1">Aktuelle Positionen aller Fahrzeuge · alle 12 Sekunden.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={toggleSim} data-testid="tracking-sim-toggle"
            className={simEnabled
              ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              : "border-[#E11226] text-[#E11226] hover:bg-red-50"}>
            <Activity size={14} className="mr-1.5" />
            Mock {simEnabled ? "AN" : "AUS"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPaused((p) => !p)} data-testid="tracking-pause">
            {paused ? <Activity size={14} className="mr-1.5" /> : <Pause size={14} className="mr-1.5" />}
            {paused ? "Fortsetzen" : "Pausieren"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadFleet(); if (selectedId) loadTrack(selectedId); }} data-testid="tracking-refresh">
            <RefreshCw size={14} className="mr-1.5" /> Aktualisieren
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat label="Verfolgt" value={stats.total} icon={CarIcon} testid="stat-total" />
        <Stat label="Parkend" value={stats.parked} icon={Pause} color="#737373" testid="stat-parked" />
        <Stat label="Stadt" value={stats.city} icon={Gauge} color="#0891B2" testid="stat-city" />
        <Stat label="Schnellstraße" value={stats.highway} icon={Navigation} color="#E11226" testid="stat-highway" />
        <Stat label="Warnungen" value={stats.alerts} icon={AlertTriangle} color="#D97706" testid="stat-alerts" alert />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <aside className="lg:col-span-4 xl:col-span-3 bg-white border border-[#E5E5E5] rounded-sm overflow-hidden flex flex-col" style={{ maxHeight: "680px" }}>
          <div className="p-3 border-b border-[#E5E5E5] space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <Input placeholder="Marke, Modell, Kategorie…" value={query}
                onChange={(e) => setQuery(e.target.value)} className="h-9 pl-9 text-sm" data-testid="tracking-search" />
            </div>
            <div className="flex flex-wrap gap-1">
              {[["all","Alle"],["parked","Parkend"],["city","Stadt"],["highway","Highway"],["alert","Alerts"]].map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)}
                  className={`px-2 py-1 text-[11px] font-semibold rounded-sm uppercase tracking-wider border transition ${
                    filter === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#A3A3A3]"
                  }`} data-testid={`tracking-filter-${k}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-[#E5E5E5]" data-testid="tracking-list">
            {loading ? (
              <div className="p-6 text-sm text-[#525252]">Lädt Flotte…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-sm text-[#525252] text-center">Keine Fahrzeuge.</div>
            ) : filtered.map((v) => {
              const isActive = v.vehicle_id === selectedId;
              return (
                <button key={v.vehicle_id} onClick={() => selectVehicle(v.vehicle_id)}
                  className={`w-full text-left px-3 py-3 hover:bg-[#FAFAFA] transition flex items-center gap-3 ${isActive ? "bg-[#FEE2E5]" : ""}`}
                  data-testid={`tracking-vehicle-${v.vehicle_id}`}>
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLOR[v.status] || "#A3A3A3" }} />
                  <img src={v.image_url} alt="" className="w-12 h-10 object-cover rounded-sm border border-[#E5E5E5] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] truncate">{v.brand}</div>
                    <div className="font-semibold text-[#0A0A0A] truncate text-sm">{v.name}</div>
                    <div className="text-[11px] text-[#525252] flex items-center gap-2 mt-0.5">
                      <span>{Math.round(v.speed_kmh)} km/h</span>
                      <span>·</span>
                      <span>{STATUS_LABEL[v.status] || v.status}</span>
                    </div>
                  </div>
                  {v.geofence_alert && <AlertTriangle size={14} className="text-amber-600 shrink-0" />}
                </button>
              );
            })}
          </div>
        </aside>

        <section className="lg:col-span-8 xl:col-span-9">
          <div className="relative bg-white border border-[#E5E5E5] rounded-sm overflow-hidden" style={{ height: "580px" }}>
            <div ref={mapContainerRef} style={{ height: "100%", width: "100%" }} />
            <div className="absolute top-3 right-3 z-10 bg-white/95 backdrop-blur border border-[#E5E5E5] rounded-sm px-3 py-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#0A0A0A]">
              <span className={`w-2 h-2 rounded-full ${paused ? "bg-[#A3A3A3]" : "bg-[#16A34A] animate-pulse"}`} />
              {paused ? "Pausiert" : "Live"}
            </div>
          </div>

          {selected && (
            <div className="mt-4 bg-white border border-[#E5E5E5] rounded-sm p-5" data-testid="tracking-detail">
              <div className="flex items-start gap-4 flex-wrap">
                <img src={selected.image_url} alt="" className="w-28 h-20 object-cover rounded-sm border border-[#E5E5E5]" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-xs uppercase tracking-wider text-[#A3A3A3]">{selected.brand}</div>
                    <Badge className="bg-[#F4F4F4] text-[#262626] border-0">{selected.category}</Badge>
                    {selected.geofence_alert && (
                      <Badge className="bg-amber-100 text-amber-800 border-0 gap-1.5">
                        <AlertTriangle size={11} /> Geofence-Warnung
                      </Badge>
                    )}
                  </div>
                  <div className="font-display font-bold text-xl text-[#0A0A0A] mt-0.5">{selected.name}</div>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <DetailKV label="Status" value={STATUS_LABEL[selected.status] || selected.status} color={STATUS_COLOR[selected.status]} icon={Activity} />
                    <DetailKV label="Geschwindigkeit" value={`${Math.round(selected.speed_kmh)} km/h`} icon={Gauge} />
                    <DetailKV label="Distanz Standort" value={`${selected.fence_km.toFixed(1)} km`} icon={MapPin} />
                    <DetailKV label="Letztes Signal" value={new Date(selected.ts).toLocaleTimeString("de-DE")} icon={RouteIcon} />
                  </div>
                  <div className="mt-3 text-[11px] text-[#737373]">
                    {selected.lat.toFixed(5)}, {selected.lng.toFixed(5)} · Kurs {Math.round(selected.heading)}° · {selected.source}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => selectVehicle(null)} data-testid="tracking-clear">
                  <X size={14} className="mr-1" /> Auswahl aufheben
                </Button>
              </div>
              <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
                <div className="text-[11px] uppercase tracking-wider text-[#525252] flex items-center gap-1.5">
                  <RouteIcon size={12} />
                  {track.length > 1
                    ? `Letzte ${track.length} Positionen · rote Linie auf der Karte`
                    : "Noch keine Track-Daten — warte auf nächsten Simulator-Tick (15 s)"}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color = "#0A0A0A", testid, alert }) {
  return (
    <div className={`bg-white border rounded-sm p-3 flex items-center gap-3 ${alert && value > 0 ? "border-amber-300 bg-amber-50/50" : "border-[#E5E5E5]"}`} data-testid={testid}>
      <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${color}18`, color }}>
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</div>
        <div className="font-display font-bold text-xl text-[#0A0A0A]">{value}</div>
      </div>
    </div>
  );
}

function DetailKV({ label, value, icon: Icon, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] flex items-center gap-1"><Icon size={11} /> {label}</div>
      <div className="font-semibold text-[#0A0A0A] mt-0.5" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}
